import { useTheme } from '@mui/material';
import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { FeatureFlagsContext } from '../../../contexts/FeatureFlagsContext';
import { useChatBotOverlay } from '../../../contexts/OverlayOffsetContext';
import { COLORS } from '../../../theme/colors';

const CHATBOT_LAYER_MANAGED_ATTR = 'data-chatbot-layer-managed';
const ORIGINAL_Z_INDEX_ATTR = 'data-chatbot-original-z-index';
const ORIGINAL_Z_INDEX_PRIORITY_ATTR = 'data-chatbot-original-z-index-priority';
const CHATBOT_PROTECTED_OVERLAY_Z_INDEX = '1';
const LEARNER_DASHBOARD_PATH_PREFIX = '/dashboard/learner';

const getSafeSelectorValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const hasChatbotText = (value: string | null | undefined): boolean => Boolean(value?.toLowerCase().includes('chat'));

const isPositionedCandidate = (element: HTMLElement): boolean => {
  const computedStyle = window.getComputedStyle(element);

  return ['fixed', 'absolute'].includes(computedStyle.position);
};

const getOutermostPositionedAncestor = (element: HTMLElement): HTMLElement | null => {
  let current: HTMLElement | null = element;
  let positionedAncestor: HTMLElement | null = null;

  while (current && current !== document.body) {
    if (isPositionedCandidate(current)) {
      positionedAncestor = current;
    }

    current = current.parentElement;
  }

  return positionedAncestor;
};

const isLikelyChatbotElement = (element: HTMLElement, chatbotUrl: string): boolean => {
  if (element instanceof HTMLIFrameElement && element.src.includes(chatbotUrl)) {
    return true;
  }

  const datasetValues = Object.values(element.dataset);

  return (
    hasChatbotText(element.id) ||
    hasChatbotText(element.className?.toString()) ||
    hasChatbotText(element.getAttribute('aria-label')) ||
    hasChatbotText(element.getAttribute('title')) ||
    datasetValues.some(hasChatbotText)
  );
};

const getChatbotLayerCandidates = (chatbotUrl: string): HTMLElement[] => {
  const candidates = new Set<HTMLElement>();
  const safeChatbotUrl = getSafeSelectorValue(chatbotUrl);
  const chatbotIframes = document.body.querySelectorAll<HTMLElement>(`iframe[src*="${safeChatbotUrl}"]`);
  const possibleChatbotElements = document.body.querySelectorAll<HTMLElement>('[id], [class], [aria-label], [title]');

  [...Array.from(chatbotIframes), ...Array.from(possibleChatbotElements)].forEach((element) => {
    if (!isLikelyChatbotElement(element, chatbotUrl)) {
      return;
    }

    const positionedElement = getOutermostPositionedAncestor(element);

    if (positionedElement) {
      candidates.add(positionedElement);
    }
  });

  return Array.from(candidates);
};

const applyChatbotProtectedLayer = (chatbotUrl: string, isProtectedOverlayActive: boolean): void => {
  if (!isProtectedOverlayActive) {
    restoreManagedChatbotElements();

    return;
  }

  const candidates = getChatbotLayerCandidates(chatbotUrl);

  document.documentElement.toggleAttribute('data-chatbot-protected-overlay-active', true);

  candidates.forEach((element) => {
    const managedElement = element;
    const hasOriginalZIndex = managedElement.hasAttribute(ORIGINAL_Z_INDEX_ATTR);
    const originalZIndex = managedElement.getAttribute(ORIGINAL_Z_INDEX_ATTR);
    const originalZIndexPriority = managedElement.getAttribute(ORIGINAL_Z_INDEX_PRIORITY_ATTR) || undefined;

    if (!isProtectedOverlayActive) {
      if (hasOriginalZIndex && originalZIndex) {
        managedElement.style.setProperty('z-index', originalZIndex, originalZIndexPriority);
      } else if (hasOriginalZIndex) {
        managedElement.style.removeProperty('z-index');
      }

      managedElement.removeAttribute(ORIGINAL_Z_INDEX_ATTR);
      managedElement.removeAttribute(ORIGINAL_Z_INDEX_PRIORITY_ATTR);
      managedElement.removeAttribute(CHATBOT_LAYER_MANAGED_ATTR);

      return;
    }

    if (!hasOriginalZIndex) {
      managedElement.setAttribute(ORIGINAL_Z_INDEX_ATTR, managedElement.style.zIndex);
      managedElement.setAttribute(ORIGINAL_Z_INDEX_PRIORITY_ATTR, managedElement.style.getPropertyPriority('z-index'));
    }

    managedElement.setAttribute(CHATBOT_LAYER_MANAGED_ATTR, 'true');
    managedElement.style.setProperty('z-index', CHATBOT_PROTECTED_OVERLAY_Z_INDEX, 'important');
  });
};

const restoreManagedChatbotElements = (): void => {
  document.documentElement.removeAttribute('data-chatbot-protected-overlay-active');

  document.body.querySelectorAll<HTMLElement>(`[${CHATBOT_LAYER_MANAGED_ATTR}="true"]`).forEach((element) => {
    const managedElement = element;
    const originalZIndex = managedElement.getAttribute(ORIGINAL_Z_INDEX_ATTR);
    const originalZIndexPriority = managedElement.getAttribute(ORIGINAL_Z_INDEX_PRIORITY_ATTR) || undefined;

    if (originalZIndex) {
      managedElement.style.setProperty('z-index', originalZIndex, originalZIndexPriority);
    } else {
      managedElement.style.removeProperty('z-index');
    }

    managedElement.removeAttribute(ORIGINAL_Z_INDEX_ATTR);
    managedElement.removeAttribute(ORIGINAL_Z_INDEX_PRIORITY_ATTR);
    managedElement.removeAttribute(CHATBOT_LAYER_MANAGED_ATTR);
  });
};

const isLearnerDashboardPath = (pathname: string): boolean => pathname.startsWith(LEARNER_DASHBOARD_PATH_PREFIX);

const ChatBotWidget = (): null => {
  const theme = useTheme();
  const { features } = useContext(FeatureFlagsContext);
  const { isProtectedOverlayActive } = useChatBotOverlay();
  const { pathname } = useLocation();
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  const chatbotConfig = features.qsemShowChatBot;
  const brandColor = theme.palette.text.redViolet ?? COLORS.chatBotBrandColor;

  useEffect(() => {
    if (!chatbotConfig?.url) {
      return undefined;
    }

    const script = document.createElement('script');

    script.src = `${chatbotConfig.url}/static/widget.js`;
    script.dataset.apiUrl = chatbotConfig.url;
    script.dataset.brandColor = brandColor;
    script.dataset.institutionName = chatbotConfig.institutionName || 'la universidad';

    if (chatbotConfig.position) {
      script.dataset.position = chatbotConfig.position;
    }
    if (chatbotConfig.offsetX) {
      script.dataset.offsetX = chatbotConfig.offsetX;
    }
    if (chatbotConfig.offsetY) {
      script.dataset.offsetY = chatbotConfig.offsetY;
    }

    script.async = true;

    document.body.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current && document.body.contains(scriptRef.current)) {
        document.body.removeChild(scriptRef.current);
      }

      scriptRef.current = null;
    };
  }, [
    chatbotConfig?.url,
    chatbotConfig?.institutionName,
    chatbotConfig?.position,
    chatbotConfig?.offsetX,
    chatbotConfig?.offsetY,
    brandColor,
  ]);

  useEffect(() => {
    if (!chatbotConfig?.url || !isLearnerDashboardPath(pathname)) {
      restoreManagedChatbotElements();

      return undefined;
    }

    applyChatbotProtectedLayer(chatbotConfig.url, isProtectedOverlayActive);

    const observer = new MutationObserver(() =>
      applyChatbotProtectedLayer(chatbotConfig.url, isProtectedOverlayActive),
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      restoreManagedChatbotElements();
    };
  }, [chatbotConfig?.url, isProtectedOverlayActive, pathname]);

  return null;
};

export default ChatBotWidget;
